import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import {BehaviorSubject, interval, Subject, Subscription} from 'rxjs';
import {TrackPlayerOptions, TrackPlayerState, TrackPoint} from "./index";

@Injectable({
  providedIn: 'root'
})
export class TrackPlayerService {

  private map: L.Map | null = null;
  private trackPoints: TrackPoint[] = [];
  private currentMarker: L.Marker | null = null;
  private trailPolyline: L.Polyline | null = null;
  private directionMarkers: L.Marker[] = [];

  private playbackSubscription: Subscription | null = null;
  private currentIndex = 0;
  private startTime = 0;
  private pausedTime = 0;
  private currentBearing = 0; // Track current bearing for car rotation

  // Default options
  private options: TrackPlayerOptions = {
    speed: 1,
    showTrail: true,
    trailColor: '#3388ff',
    trailWeight: 3,
    showDirection: true,
    interpolate: true,
    loop: false
  };

  // State subjects
  private stateSubject = new BehaviorSubject<TrackPlayerState>({
    isPlaying: false,
    isPaused: false,
    currentIndex: 0,
    totalPoints: 0,
    currentPosition: null,
    progress: 0,
    elapsedTime: 0,
    totalTime: 0
  });

  private positionUpdateSubject = new Subject<TrackPoint>();
  private playbackEndSubject = new Subject<void>();

  constructor() {}

  // Observables for components to subscribe to
  get state$() {
    return this.stateSubject.asObservable();
  }

  get positionUpdate$() {
    return this.positionUpdateSubject.asObservable();
  }

  get playbackEnd$() {
    return this.playbackEndSubject.asObservable();
  }

  /**
   * Create a car marker with rotation capability
   */
  private createCarIcon(bearing: number = 0): L.DivIcon {
    return L.divIcon({
      className: 'car-marker-icon',
      html: `
        <div style="
          transform: rotate(${bearing}deg);
          transform-origin: center;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <img src="https://finek.com.tn/images/cars/c1x0.png"
               style="width: 28px; height: 28px; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));"
               alt="car" />
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });
  }

  /**
   * Initialize the track player with a Leaflet map
   */
  initialize(map: L.Map): void {
    console.log("the map initialized");
    this.map = map;
  }

  /**
   * Load track data
   */
  loadTrack(trackPoints: TrackPoint[], options?: Partial<TrackPlayerOptions>): void {
    if (!this.map) {
      throw new Error('TrackPlayer must be initialized with a map first');
    }

    this.stop();
    this.clearTrack();

    this.trackPoints = [...trackPoints];
    this.options = { ...this.options, ...options };
    this.currentIndex = 0;

    // Calculate total time if timestamps are available
    const totalTime = this.calculateTotalTime();

    this.updateState({
      totalPoints: this.trackPoints.length,
      totalTime,
      currentIndex: 0,
      progress: 0,
      elapsedTime: 0,
      currentPosition: this.trackPoints[0] || null
    });

    // Show initial position
    if (this.trackPoints.length > 0) {
      // Calculate initial bearing if we have at least 2 points
      if (this.trackPoints.length > 1) {
        this.currentBearing = this.calculateBearing(this.trackPoints[0], this.trackPoints[1]);
      }
      this.updateMarkerPosition(this.trackPoints[0]);
      this.map.setView([this.trackPoints[0].lat, this.trackPoints[0].lng], this.map.getZoom());
    }

    // Show full trail if enabled
    if (this.options.showTrail) {
      this.showFullTrail();
    }
  }

  /**
   * Start or resume playback
   */
  play(): void {
    if (!this.trackPoints.length || !this.map) return;

    if (this.stateSubject.value.isPaused) {
      this.resume();
      return;
    }

    this.startTime = Date.now();
    this.currentIndex = 0;

    this.updateState({ isPlaying: true, isPaused: false });
    this.startPlayback();
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (this.playbackSubscription) {
      this.playbackSubscription.unsubscribe();
      this.playbackSubscription = null;
    }

    this.pausedTime = Date.now() - this.startTime;
    this.updateState({ isPlaying: false, isPaused: true });
  }

  /**
   * Resume playback
   */
  resume(): void {
    if (!this.stateSubject.value.isPaused) return;

    // Calculate the correct start time based on current position and paused time
    this.startTime = Date.now() - this.pausedTime;
    this.updateState({ isPlaying: true, isPaused: false });
    this.startPlayback();
  }

  /**
   * Stop playback and reset to beginning
   */
  stop(): void {
    if (this.playbackSubscription) {
      this.playbackSubscription.unsubscribe();
      this.playbackSubscription = null;
    }

    this.currentIndex = 0;
    this.startTime = 0;
    this.pausedTime = 0;

    this.updateState({
      isPlaying: false,
      isPaused: false,
      currentIndex: 0,
      progress: 0,
      elapsedTime: 0,
      currentPosition: this.trackPoints[0] || null
    });

    if (this.trackPoints.length > 0) {
      // Reset bearing to initial direction
      if (this.trackPoints.length > 1) {
        this.currentBearing = this.calculateBearing(this.trackPoints[0], this.trackPoints[1]);
      }
      this.updateMarkerPosition(this.trackPoints[0]);
    }
  }

  /**
   * Seek to specific position (0-100)
   */
  seekTo(percentage: number): void {
    if (!this.trackPoints.length) return;

    const targetIndex = Math.floor((percentage / 100) * (this.trackPoints.length - 1));
    this.currentIndex = Math.max(0, Math.min(targetIndex, this.trackPoints.length - 1));

    const currentPoint = this.trackPoints[this.currentIndex];

    // Update bearing based on direction to next point
    if (this.currentIndex < this.trackPoints.length - 1) {
      const nextPoint = this.trackPoints[this.currentIndex + 1];
      this.currentBearing = this.calculateBearing(currentPoint, nextPoint);
    } else if (this.currentIndex > 0) {
      const prevPoint = this.trackPoints[this.currentIndex - 1];
      this.currentBearing = this.calculateBearing(prevPoint, currentPoint);
    }

    this.updateMarkerPosition(currentPoint);

    // Calculate elapsed time based on the new position
    const elapsedTime = this.getElapsedTimeToIndex(this.currentIndex) / 1000;

    // If currently playing, adjust the start time to maintain proper timing
    if (this.stateSubject.value.isPlaying) {
      this.startTime = Date.now() - (this.getElapsedTimeToIndex(this.currentIndex) / this.options.speed);
    } else if (this.stateSubject.value.isPaused) {
      // Update paused time to reflect new position
      this.pausedTime = this.getElapsedTimeToIndex(this.currentIndex) / this.options.speed;
    }

    this.updateState({
      currentIndex: this.currentIndex,
      progress: percentage,
      currentPosition: currentPoint,
      elapsedTime: elapsedTime
    });

    this.positionUpdateSubject.next(currentPoint);

    // Update trail if in progress
    if (this.options.showTrail && this.trailPolyline) {
      this.updateTrail();
    }
  }

  /**
   * Set playback speed - SIMPLIFIED VERSION
   */
  setSpeed(speed: number): void {
    const oldSpeed = this.options.speed;
    this.options.speed = Math.max(0.1, speed);

    if (this.stateSubject.value.isPlaying) {
      const currentElapsedTime = (Date.now() - this.startTime) * oldSpeed;
      this.startTime = Date.now() - (currentElapsedTime / this.options.speed);
    } else if (this.stateSubject.value.isPaused) {
      this.pausedTime = this.pausedTime * (oldSpeed / this.options.speed);
    }
  }
  /**
   * Get current speed
   */
  getSpeed(): number {
    return this.options.speed;
  }

  /**
   * Update options
   */
  updateOptions(options: Partial<TrackPlayerOptions>): void {
    this.options = { ...this.options, ...options };

    if (options.showTrail !== undefined) {
      if (options.showTrail && this.trackPoints.length > 0) {
        this.showFullTrail();
      } else {
        this.hideTrail();
      }
    }

    if (options.markerIcon && this.currentMarker) {
      this.currentMarker.setIcon(options.markerIcon);
    }
  }

  /**
   * Clear all track elements from map
   */
  clearTrack(): void {
    if (this.currentMarker) {
      this.map?.removeLayer(this.currentMarker);
      this.currentMarker = null;
    }

    this.hideTrail();
    this.clearDirectionMarkers();
  }

  /**
   * Get current track points
   */
  getTrackPoints(): TrackPoint[] {
    return [...this.trackPoints];
  }

  /**
   * Get current state
   */
  getCurrentState(): TrackPlayerState {
    return this.stateSubject.value;
  }

  private startPlayback(): void {
    if (this.playbackSubscription) {
      this.playbackSubscription.unsubscribe();
    }

    // Use 60 FPS for smooth animation
    this.playbackSubscription = interval(1000 / 60).subscribe(() => {
      this.updatePlayback();
    });
  }

  private updatePlayback(): void {
    if (this.currentIndex >= this.trackPoints.length - 1) {
      this.handlePlaybackEnd();
      return;
    }

    const elapsed = (Date.now() - this.startTime) * this.options.speed;
    const currentPoint = this.trackPoints[this.currentIndex];
    const nextPoint = this.trackPoints[this.currentIndex + 1];

    if (!currentPoint || !nextPoint) return;

    // Calculate when to move to next point
    const pointDuration = this.getPointDuration(currentPoint, nextPoint);
    const elapsedToCurrentIndex = this.getElapsedTimeToIndex(this.currentIndex);
    const pointElapsed = elapsed - elapsedToCurrentIndex;

    if (pointElapsed >= pointDuration) {
      this.currentIndex++;
      const newPoint = this.trackPoints[this.currentIndex];

      // Update bearing based on movement direction
      this.currentBearing = this.calculateBearing(currentPoint, newPoint);

      this.updateMarkerPosition(newPoint);
      this.updateTrail();

      const progress = (this.currentIndex / (this.trackPoints.length - 1)) * 100;
      const elapsedTimeSeconds = elapsed / 1000;

      this.updateState({
        currentIndex: this.currentIndex,
        progress,
        elapsedTime: elapsedTimeSeconds,
        currentPosition: newPoint
      });

      this.positionUpdateSubject.next(newPoint);
    } else if (this.options.interpolate && pointElapsed >= 0) {
      // Interpolate between current and next point
      const ratio = Math.max(0, Math.min(1, pointElapsed / pointDuration));
      const interpolatedPoint = this.interpolatePoints(currentPoint, nextPoint, ratio);

      // Update bearing for smooth rotation during interpolation
      this.currentBearing = this.calculateBearing(currentPoint, nextPoint);

      this.updateMarkerPosition(interpolatedPoint);
      this.positionUpdateSubject.next(interpolatedPoint);
    }
  }

  private handlePlaybackEnd(): void {
    if (this.options.loop) {
      this.currentIndex = 0;
      this.startTime = Date.now();
      // Reset bearing for loop
      if (this.trackPoints.length > 1) {
        this.currentBearing = this.calculateBearing(this.trackPoints[0], this.trackPoints[1]);
      }
    } else {
      this.stop();
      this.playbackEndSubject.next();
    }
  }

  private updateMarkerPosition(point: TrackPoint): void {
    if (!this.map) return;

    if (!this.currentMarker) {
      const carIcon = this.createCarIcon(this.currentBearing);
      this.currentMarker = L.marker([point.lat, point.lng], { icon: carIcon })
        .addTo(this.map);
    } else {
      // Update position
      this.currentMarker.setLatLng([point.lat, point.lng]);

      // Update car icon with new bearing
      const updatedCarIcon = this.createCarIcon(this.currentBearing);
      this.currentMarker.setIcon(updatedCarIcon);
    }

    // Focus the map on the current marker position
    this.map.setView([point.lat, point.lng], this.map.getZoom(), {
      animate: true,
      duration: 0.1 // Short animation duration for smooth following
    });
  }

  private showFullTrail(): void {
    if (!this.map || !this.trackPoints.length) return;

    const latlngs: L.LatLngExpression[] = this.trackPoints.map(p => [p.lat, p.lng]);

    this.trailPolyline = L.polyline(latlngs, {
      color: this.options.trailColor,
      weight: this.options.trailWeight,
      opacity: 0.7
    }).addTo(this.map);
  }

  private updateTrail(): void {
    if (!this.options.showTrail || !this.map || !this.trackPoints.length) return;

    if (this.trailPolyline) {
      this.map.removeLayer(this.trailPolyline);
    }

    const completedPoints = this.trackPoints.slice(0, this.currentIndex + 1);
    const latlngs: L.LatLngExpression[] = completedPoints.map(p => [p.lat, p.lng]);

    if (latlngs.length > 1) {
      this.trailPolyline = L.polyline(latlngs, {
        color: this.options.trailColor,
        weight: this.options.trailWeight,
        opacity: 0.7
      }).addTo(this.map);
    }
  }

  private hideTrail(): void {
    if (this.trailPolyline && this.map) {
      this.map.removeLayer(this.trailPolyline);
      this.trailPolyline = null;
    }
  }

  private clearDirectionMarkers(): void {
    if (!this.map) return;

    this.directionMarkers.forEach(marker => {
      this.map!.removeLayer(marker);
    });
    this.directionMarkers = [];
  }

  private calculateTotalTime(): number {
    if (this.trackPoints.length < 2) return 0;

    const firstPoint = this.trackPoints[0];
    const lastPoint = this.trackPoints[this.trackPoints.length - 1];

    if (firstPoint.timestamp && lastPoint.timestamp) {
      return (lastPoint.timestamp - firstPoint.timestamp) / 1000;
    }

    // Fallback: estimate based on distance and average speed
    let totalDistance = 0;
    for (let i = 1; i < this.trackPoints.length; i++) {
      totalDistance += this.calculateDistance(this.trackPoints[i - 1], this.trackPoints[i]);
    }

    // Assume average speed of 50 km/h if no timestamps
    const avgSpeedKmh = 50;
    return (totalDistance / avgSpeedKmh) * 3600;
  }

  private getPointDuration(current: TrackPoint, next: TrackPoint): number {
    if (current.timestamp && next.timestamp) {
      return next.timestamp - current.timestamp;
    }

    // Fallback: calculate based on distance and estimated speed
    const distance = this.calculateDistance(current, next);
    const estimatedSpeedKmh = 50;
    return (distance / estimatedSpeedKmh) * 3600 * 1000; // Convert to milliseconds
  }

  private getElapsedTimeToIndex(index: number): number {
    let elapsed = 0;

    for (let i = 0; i < index && i < this.trackPoints.length - 1; i++) {
      const current = this.trackPoints[i];
      const next = this.trackPoints[i + 1];
      if (current && next) {
        elapsed += this.getPointDuration(current, next);
      }
    }

    return elapsed;
  }

  private interpolatePoints(p1: TrackPoint, p2: TrackPoint, ratio: number): TrackPoint {
    return {
      lat: p1.lat + (p2.lat - p1.lat) * ratio,
      lng: p1.lng + (p2.lng - p1.lng) * ratio,
      timestamp: p1.timestamp ? p1.timestamp + ((p2.timestamp || p1.timestamp) - p1.timestamp) * ratio : undefined,
      speed: p1.speed ? p1.speed + ((p2.speed || p1.speed) - p1.speed) * ratio : undefined,
      altitude: p1.altitude ? p1.altitude + ((p2.altitude || p1.altitude) - p1.altitude) * ratio : undefined
    };
  }

  private calculateDistance(p1: TrackPoint, p2: TrackPoint): number {
    const R = 6371; // Earth's radius in km
    const dLat = this.deg2rad(p2.lat - p1.lat);
    const dLng = this.deg2rad(p2.lng - p1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(p1.lat)) * Math.cos(this.deg2rad(p2.lat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private calculateBearing(p1: TrackPoint, p2: TrackPoint): number {
    const dLng = this.deg2rad(p2.lng - p1.lng);
    const lat1 = this.deg2rad(p1.lat);
    const lat2 = this.deg2rad(p2.lat);

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    return (this.rad2deg(Math.atan2(y, x)) + 360) % 360;
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  private rad2deg(rad: number): number {
    return rad * (180 / Math.PI);
  }

  private updateState(partialState: Partial<TrackPlayerState>): void {
    const currentState = this.stateSubject.value;
    const newState = { ...currentState, ...partialState };
    this.stateSubject.next(newState);
  }

  ngOnDestroy(): void {
    this.stop();
    this.clearTrack();
  }
}
